import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { printScreenshots } from '@messenger/utils'
import type {
  ProofShareGalleryProduct,
  ProofShareValidation,
} from '@messenger/types'
import Text from '@/components/priority/Text/Text'
import Button from '@/components/priority/Button/Button'
import { getProofShareByToken, getProofShareVersion } from '@/api/proofShare'
import * as S from '@/components/proof/ProofGallery.styled'
import * as Share from '@/components/proof/ProofShareGallery.styled'

type ProofShareGalleryRouteParameters = {
  token?: string
}

// One product's screenshot carousel — same viewer controls as the single-pdf
// ProofGallery, shown for the active product tab.
const ProductGallerySection = ({
  product,
}: {
  product: ProofShareGalleryProduct
}) => {
  const { t } = useTranslation()
  const [activeScreenshotIndex, setActiveScreenshotIndex] = useState(0)

  const totalScreenshotCount = product.screenshotUrls.length
  const displayedPageNumber = activeScreenshotIndex + 1
  const activeScreenshotUrl = product.screenshotUrls[activeScreenshotIndex]

  const handlePrint = useCallback(() => {
    printScreenshots(product.screenshotUrls)
  }, [product.screenshotUrls])

  return (
    <>
      <S.ProofGalleryHeader>
        <Text
          as="h2"
          size="22px"
          weight="600"
          color="#1b1f3b"
          style={{ margin: 0 }}
        >
          {product.productName || t('proofShareGallery.defaultProductName')}
        </Text>
        <S.ProofGalleryHeaderActions>
          <Text size="16px" weight="500" color="#4a4f68">
            {t('proofGallery.pageIndicatorText', {
              currentPageNumber: displayedPageNumber,
              totalPageCount: totalScreenshotCount,
            })}
          </Text>
          <Button
            type="button"
            primary
            padding="8px 16px"
            onClick={handlePrint}
            aria-label={t('proofGallery.printAriaLabel')}
          >
            {t('proofGallery.printLabel')}
          </Button>
        </S.ProofGalleryHeaderActions>
      </S.ProofGalleryHeader>

      <S.ProofGalleryImageWrapper>
        <S.ProofGalleryImage
          src={activeScreenshotUrl}
          alt={t('proofGallery.imageAltText', {
            pageNumber: displayedPageNumber,
          })}
        />
      </S.ProofGalleryImageWrapper>

      <S.ProofGalleryNavigation
        aria-label={t('proofGallery.navigationAriaLabel')}
      >
        <Button
          type="button"
          center
          primary
          padding="12px 24px"
          minWidth="140px"
          onClick={() => setActiveScreenshotIndex(activeScreenshotIndex - 1)}
          disabled={activeScreenshotIndex === 0}
          aria-label={t('proofGallery.previousButtonAriaLabel')}
        >
          {t('proofGallery.previousButtonLabel')}
        </Button>
        <Button
          type="button"
          center
          primary
          padding="12px 24px"
          minWidth="140px"
          onClick={() => setActiveScreenshotIndex(activeScreenshotIndex + 1)}
          disabled={activeScreenshotIndex === totalScreenshotCount - 1}
          aria-label={t('proofGallery.nextButtonAriaLabel')}
        >
          {t('proofGallery.nextButtonLabel')}
        </Button>
      </S.ProofGalleryNavigation>
    </>
  )
}

const PANEL_ID = 'proof-share-tabpanel'
const tabId = (orderProductId: string) => `proof-share-tab-${orderProductId}`

// Public recipient view for a tokenized MDL proof share. Mirrors the M3 share
// page layout: centered header (funeral home, in-memory-of, expiration), then
// a card with one tab per product — only the active product's gallery renders.
export const ProofShareGallery = () => {
  const { token } = useParams<ProofShareGalleryRouteParameters>()
  const { t, i18n } = useTranslation()

  const [shareValidation, setShareValidation] =
    useState<ProofShareValidation | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [activeProductIndex, setActiveProductIndex] = useState(0)
  // Newer-proof detection: baseline fingerprint of what the share resolved to
  // at load; polling compares against it and offers a refresh on change.
  const versionBaselineRef = useRef<string | null>(null)
  const [hasNewerVersion, setHasNewerVersion] = useState(false)
  const [reloadCounter, setReloadCounter] = useState(0)

  useEffect(() => {
    let requestIsActive = true

    const loadProofShare = async () => {
      setIsLoading(true)
      setErrorMessage(null)
      setShareValidation(null)
      setActiveProductIndex(0)

      if (!token) {
        if (requestIsActive) {
          setErrorMessage(t('proofShareGallery.invalidLinkError'))
          setIsLoading(false)
        }
        return
      }

      try {
        const response = await getProofShareByToken(token)
        if (requestIsActive) {
          setShareValidation(response.data)
        }
      } catch (error) {
        console.error(
          'error_loading_proof_share',
          (error as Error)?.message || 'Unknown error',
        )
        if (requestIsActive) {
          setErrorMessage(t('proofGallery.genericErrorMessage'))
        }
      } finally {
        if (requestIsActive) {
          setIsLoading(false)
        }
      }
    }

    void loadProofShare()

    return () => {
      requestIsActive = false
    }
  }, [token, t, reloadCounter])

  // Poll a cheap version fingerprint while a valid share is on screen; when a
  // newer proof lands (director re-edited a product after the page opened),
  // surface the refresh banner instead of silently showing stale screenshots.
  useEffect(() => {
    if (!token || !shareValidation?.valid) return

    let pollIsActive = true
    const checkForNewerVersion = async () => {
      try {
        const { data } = await getProofShareVersion(token)
        if (!pollIsActive || !data.valid) return
        if (versionBaselineRef.current === null) {
          versionBaselineRef.current = data.fingerprint
          return
        }
        if (data.fingerprint !== versionBaselineRef.current) {
          setHasNewerVersion(true)
        }
      } catch {
        // Polling is best-effort; a failed probe never disturbs the gallery.
      }
    }

    void checkForNewerVersion()
    const interval = setInterval(checkForNewerVersion, 30_000)

    return () => {
      pollIsActive = false
      clearInterval(interval)
    }
  }, [token, shareValidation?.valid])

  const handleRefreshProofs = () => {
    versionBaselineRef.current = null
    setHasNewerVersion(false)
    setReloadCounter((count) => count + 1)
  }

  if (isLoading) {
    return (
      <S.ProofGalleryMessageWrapper>
        <Text size="18px" weight="500" align="center" color="#1b1f3b">
          {t('proofGallery.loadingMessage')}
        </Text>
      </S.ProofGalleryMessageWrapper>
    )
  }

  const invalidShareMessage =
    errorMessage ??
    (shareValidation && !shareValidation.valid
      ? shareValidation.errorCode === 'expired'
        ? t('proofShareGallery.expiredLinkError')
        : shareValidation.errorCode === 'revoked'
          ? t('proofShareGallery.revokedLinkError')
          : t('proofShareGallery.invalidLinkError')
      : null)

  if (invalidShareMessage || !shareValidation?.valid) {
    return (
      <S.ProofGalleryMessageWrapper>
        <Text
          role="alert"
          size="18px"
          weight="500"
          align="center"
          color="#1b1f3b"
        >
          {invalidShareMessage ?? t('proofShareGallery.invalidLinkError')}
        </Text>
      </S.ProofGalleryMessageWrapper>
    )
  }

  const { products } = shareValidation

  if (products.length === 0) {
    return (
      <S.ProofGalleryMessageWrapper>
        <Text
          role="alert"
          size="18px"
          weight="500"
          align="center"
          color="#1b1f3b"
        >
          {t('proofGallery.noProofAvailableMessage')}
        </Text>
      </S.ProofGalleryMessageWrapper>
    )
  }

  const safeProductIndex = Math.min(activeProductIndex, products.length - 1)
  const activeProduct = products[safeProductIndex]
  const hasMultipleProducts = products.length > 1

  const productLabel = (product: ProofShareGalleryProduct): string =>
    product.productName || t('proofShareGallery.defaultProductName')

  const expirationDate = shareValidation.expiresAt
    ? new Date(shareValidation.expiresAt).toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : ''

  return (
    <S.ProofGalleryContainer>
      <Share.ShareHeader>
        <Text
          as="h1"
          size="28px"
          weight="600"
          color="#1b1f3b"
          style={{ margin: 0 }}
        >
          {t('proofShareGallery.header.title', {
            funeralHomeName: shareValidation.funeralHomeName,
          })}
        </Text>
        <div>
          <Text size="14px" weight="600" color="#797E83" transform="uppercase">
            {t('proofShareGallery.header.inMemoryOf')}
          </Text>
          <Text
            size="22px"
            weight="500"
            color="#1b1f3b"
            style={{ fontStyle: 'italic' }}
          >
            {shareValidation.deceasedName}
          </Text>
        </div>
        <Text size="14px" color="#4a4f68">
          {t('proofShareGallery.header.description')}
        </Text>
        {expirationDate && (
          <Text size="14px" color="#4a4f68">
            {t('proofShareGallery.header.availableUntil', {
              date: expirationDate,
            })}
          </Text>
        )}
      </Share.ShareHeader>

      {hasNewerVersion && (
        <Share.NewerVersionBanner
          role="status"
          data-testid="proof-share-newer-version-banner"
        >
          <Text size="14px" weight="500" color="#1b1f3b">
            {t('proofShareGallery.newerVersion.message')}
          </Text>
          <Button
            type="button"
            primary
            padding="8px 16px"
            onClick={handleRefreshProofs}
          >
            {t('proofShareGallery.newerVersion.refresh')}
          </Button>
        </Share.NewerVersionBanner>
      )}

      <Share.GalleryCard data-testid="proof-share-gallery">
        {hasMultipleProducts && (
          <Share.ProductTabs
            role="tablist"
            aria-label={t('proofShareGallery.header.title', {
              funeralHomeName: shareValidation.funeralHomeName,
            })}
          >
            {products.map((product, index) => (
              <Share.ProductTab
                key={product.orderProductId}
                type="button"
                role="tab"
                id={tabId(product.orderProductId)}
                aria-selected={index === safeProductIndex}
                aria-controls={PANEL_ID}
                $active={index === safeProductIndex}
                onClick={() => setActiveProductIndex(index)}
                data-testid="proof-share-product-tab"
              >
                {productLabel(product)}
              </Share.ProductTab>
            ))}
          </Share.ProductTabs>
        )}

        <div
          role={hasMultipleProducts ? 'tabpanel' : undefined}
          id={hasMultipleProducts ? PANEL_ID : undefined}
          aria-labelledby={
            hasMultipleProducts
              ? tabId(activeProduct.orderProductId)
              : undefined
          }
        >
          <ProductGallerySection
            key={activeProduct.orderProductId}
            product={activeProduct}
          />
        </div>
      </Share.GalleryCard>
    </S.ProofGalleryContainer>
  )
}

export default ProofShareGallery
